import axios from 'axios';
import isDev from 'util/isDev';
import { TERMINAL_CODES } from 'auth/authErrors';

// Store user ID globally so it can be accessed from error logging
let currentUserId: string | undefined;

// Mirrors the server-side guard in server/routes/logging.js. The server is the actual control —
// it sees every client version and reads the real request header — but dropping here too saves the
// request entirely. Meta's crawler alone accounted for ~86 chunk-load reports a day.
const CRAWLER_USER_AGENT =
  /meta-externalagent|Googlebot|bingbot|Applebot|GPTBot|Bytespider|ClaudeBot|PerplexityBot|facebookexternalhit|Twitterbot|Slackbot|LinkedInBot|AhrefsBot|SemrushBot/i;

export function isCrawlerUserAgent(userAgent: string | undefined) {
  return !!userAgent && CRAWLER_USER_AGENT.test(userAgent);
}

function isCrawler() {
  return typeof navigator !== 'undefined' && isCrawlerUserAgent(navigator.userAgent);
}

/**
 * Normalize ChunkLoadError messages for better aggregation in error reporting
 * Replaces specific chunk IDs and URLs with placeholders while preserving details in context
 */
function normalizeChunkLoadError(
  errorMessage: string,
  additionalData?: Record<string, unknown>,
): { normalizedMessage: string; enhancedContext: Record<string, unknown> } {
  const enhancedContext = { ...additionalData };

  // Pattern 1: "Loading chunk 82 failed. (timeout: https://...)"
  // Pattern 2: "Loading chunk 82 failed. (error: https://...)"
  // Pattern 3: "ChunkLoadError: Loading chunk 82 failed"
  const chunkLoadPattern = /Loading chunk (\d+|[a-zA-Z0-9_-]+) failed\.?\s*(?:\((?:timeout|error):\s*(.+?)\))?/i;
  const match = errorMessage.match(chunkLoadPattern);

  if (match) {
    const chunkId = match[1];
    const chunkUrl = match[2];

    // Preserve original error details in context
    enhancedContext.errorType = 'ChunkLoadError';
    enhancedContext.originalMessage = errorMessage;
    enhancedContext.chunkId = chunkId;

    if (chunkUrl) {
      enhancedContext.chunkUrl = chunkUrl;
      // Normalize the message with placeholders
      const normalizedMessage = errorMessage
        .replace(chunkLoadPattern, 'Loading chunk [CHUNK_ID] failed. ($2: [CHUNK_URL])')
        .replace(chunkUrl, '[CHUNK_URL]');
      return { normalizedMessage, enhancedContext };
    } else {
      // No URL in the error message
      const normalizedMessage = errorMessage.replace(chunkLoadPattern, 'Loading chunk [CHUNK_ID] failed.');
      return { normalizedMessage, enhancedContext };
    }
  }

  // Check for generic ChunkLoadError without specific format
  if (
    errorMessage.toLowerCase().includes('chunkloaderror') ||
    (errorMessage.toLowerCase().includes('chunk') && errorMessage.toLowerCase().includes('failed'))
  ) {
    enhancedContext.errorType = 'ChunkLoadError';
    enhancedContext.originalMessage = errorMessage;
  }

  // Return unchanged if not a chunk load error
  return { normalizedMessage: errorMessage, enhancedContext };
}

/**
 * Set the current user ID for error logging
 * Call this after authentication
 */
export function setGcpUserId(userId: string | undefined): void {
  // extractUserId parseInt's an Auth0 claim, so an absent claim used to arrive here as the literal
  // string "NaN" — truthy, so the server's `user || 'anonymous'` fallback never caught it, and
  // every such report was attributed to a user called NaN.
  currentUserId = userId && !['NaN', '', 'undefined', 'null'].includes(userId) ? userId : undefined;
}

/**
 * Get the current user ID
 */
function getCurrentUserId(): string | undefined {
  return currentUserId;
}

/**
 * Send error data to GCP backend endpoint
 */
async function sendToGcpEndpoint(payload: {
  message: string;
  stack?: string;
  severity: string;
  additionalContext?: Record<string, unknown>;
}): Promise<void> {
  try {
    // Don't send errors in development to avoid noise
    if (isDev()) {
      console.log('[GCP Logging - Dev Mode]', payload);
      return;
    }

    if (isCrawler()) {
      return;
    }

    const errorPayload = {
      message: payload.message,
      stack: payload.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      user: getCurrentUserId(),
      severity: payload.severity,
      additionalContext: payload.additionalContext,
    };

    // Send to backend endpoint (non-blocking)
    await axios.post('/log-client-error', errorPayload, {
      timeout: 5000,
      // Don't throw on error - silent fail
      validateStatus: () => true,
    });
  } catch (error) {
    // Silent fail - don't throw errors from error logging
    if (isDev()) {
      console.error('Failed to send error to GCP:', error);
    }
  }
}

/**
 * Log an error to GCP Error Reporting
 *
 * @param error - Error object or error message
 * @param message - Optional additional message
 * @param additionalData - Optional context data
 */
export function logErrorToGcp(error: Error | any, message?: string, additionalData?: Record<string, unknown>): void {
  // Unpack nested exceptions and collect all messages
  const messages: string[] = [];

  // Add custom message first if provided
  if (message) {
    messages.push(message);
  }

  // Walk through error chain and collect all messages
  let currentError = error;
  while (currentError) {
    const msg = currentError?.message || currentError?.toString();
    if (msg && msg !== '[object Object]') {
      messages.push(msg);
    }
    // Check for nested error (common patterns: cause, error, inner)
    currentError = currentError?.cause || currentError?.error;
  }

  const errorMessage = messages.length > 0 ? messages.join(' : ') : 'Unknown error';

  // Extract stack if available
  const stack = error?.stack || undefined;

  // Check if this is a chunk loading error and add retry metadata
  const contextData = { ...additionalData };
  if (error?.isChunkLoadError) {
    contextData.chunkRetryAttempts = error.chunkRetryAttempts;
    contextData.chunkId = error.chunkId;
    contextData.networkOnline = navigator.onLine;
  }

  // Normalize ChunkLoadError messages for better aggregation
  const { normalizedMessage, enhancedContext } = normalizeChunkLoadError(errorMessage, contextData);

  // Log to console in development
  if (isDev()) {
    console.error('[GCP Error]', normalizedMessage, error, enhancedContext);
  }

  // Send to GCP (async, non-blocking)
  sendToGcpEndpoint({
    message: normalizedMessage,
    stack,
    severity: 'ERROR',
    additionalContext: enhancedContext,
  }).catch(() => {
    // Silent fail
  });
}

/**
 * Log a warning to GCP Error Reporting
 */
export function logWarningToGcp(message: string, additionalData?: Record<string, unknown>): void {
  if (isDev()) {
    console.warn('[GCP Warning]', message, additionalData);
  }

  sendToGcpEndpoint({
    message,
    severity: 'WARNING',
    additionalContext: additionalData,
  }).catch(() => {
    // Silent fail
  });
}

/**
 * Log an info message to GCP Error Reporting
 */
export function logInfoToGcp(message: string, additionalData?: Record<string, unknown>): void {
  if (isDev()) {
    console.info('[GCP Info]', message, additionalData);
  }

  sendToGcpEndpoint({
    message,
    severity: 'INFO',
    additionalContext: additionalData,
  }).catch(() => {
    // Silent fail
  });
}

export type RejectionOrigin = 'auth' | 'api' | 'unknown';

/**
 * Work out where an unhandled rejection came from.
 *
 * ApiBase rejects with a bare axios response object (not an Error), so an API failure is an object
 * carrying a numeric `status` and no `message`. Auth0 rejects with a GenericError subclass carrying
 * `.error`. Those are the two families worth telling apart.
 */
export function classifyRejectionOrigin(reason: any): RejectionOrigin {
  if (!reason || typeof reason !== 'object') return 'unknown';

  const code = reason.error;
  if (typeof code === 'string' && TERMINAL_CODES.includes(code)) return 'auth';
  if (typeof reason.name === 'string' && reason.name.endsWith('RefreshTokenError')) return 'auth';

  if (typeof reason.status === 'number') return 'api';

  return 'unknown';
}

/**
 * Set up global error handlers to capture uncaught errors
 * Call this once during app initialization
 */
export function setupGcpErrorHandlers(): void {
  // Capture uncaught errors
  window.addEventListener('error', event => {
    const error = event.error || event.message;
    logErrorToGcp(error, undefined, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', event => {
    const reason = event.reason;
    const origin = classifyRejectionOrigin(reason);
    const additionalData: Record<string, unknown> = {
      reasonType: typeof reason,
      pathname: window.location.pathname,
      origin,
    };

    // Extract useful info from the rejection reason
    if (reason instanceof Error) {
      additionalData.message = reason.message;
      additionalData.stack = reason.stack;
      additionalData.name = reason.name;
    } else if (reason && typeof reason === 'object') {
      additionalData.error = (reason as any).error;
      additionalData.errorDescription = (reason as any).error_description;
      additionalData.message = (reason as any).message;
      additionalData.status = (reason as any).status;
    } else {
      additionalData.reason = String(reason);
    }

    // Error Reporting groups by stack, so an uncaught 4xx from the API and a dead auth session
    // used to pile into the same opaque "Unhandled promise rejection" bucket. Naming the family
    // in the message splits them, which is what makes either one measurable.
    const message =
      origin === 'api'
        ? `Unhandled API rejection (status ${(reason as any)?.status ?? 'unknown'})`
        : 'Unhandled promise rejection';

    logErrorToGcp(reason instanceof Error ? reason : new Error(message), message, additionalData);
  });
}
